import Phaser from 'phaser';
import { MenuMusic } from '../audio/MenuMusic';

interface Mover { sprite: Phaser.GameObjects.Sprite; speed: number }

const BG_SOURCES: Array<[string, string]> = [
  ['menu_bg_towerblock',    'assets/menu/liverpool_bg.jpg'],
  ['menu_bg_welshstreets',  'assets/menu/menu_bg_welshstreets.jpg'],
  ['menu_bg_dereliktpub',   'assets/menu/menu_bg_dereliktpub.jpg'],
  ['menu_bg_smithdown',     'assets/menu/menu_bg_smithdown.jpg'],
  ['menu_bg_anfieldcrowd1', 'assets/menu/menu_bg_anfieldcrowd1.jpg'],
  ['menu_bg_anfieldcrowd2', 'assets/menu/menu_bg_anfieldcrowd2.jpg'],
  ['menu_bg_concertsquare', 'assets/menu/menu_bg_concertsquare.jpg']
];

const BG_HOLD_MS = 7000;
const BG_FADE_MS = 1800;
const BG_KB_ZOOM = 0.10;
const BG_KB_PAN  = 40;

export class MenuScene extends Phaser.Scene {
  private playBtn!: Phaser.GameObjects.Container;
  private cars: Mover[] = [];
  private peds: Mover[] = [];
  private titleBob!: Phaser.GameObjects.Text;
  private bgA!: Phaser.GameObjects.Image;
  private bgB!: Phaser.GameObjects.Image;
  private bgKeys: string[] = [];
  private bgIdx = 0;

  constructor() { super('MenuScene'); }

  preload() {
    for (const [key, path] of BG_SOURCES) {
      if (!this.textures.exists(key)) this.load.image(key, path);
    }
  }

  create() {
    const { width: w, height: h } = this.scale;

    // Cycling Ken-Burns photo backdrop. Two stacked images crossfade; tint + dark overlay sit on top.
    this.bgKeys = BG_SOURCES.map(([k]) => k).filter(k => this.textures.exists(k));
    this.bgA = this.add.image(w / 2, h / 2, this.bgKeys[0]).setOrigin(0.5).setTint(0x9aa3b0);
    this.bgB = this.add.image(w / 2, h / 2, this.bgKeys[0]).setOrigin(0.5).setTint(0x9aa3b0).setAlpha(0);
    this.bgIdx = 0;
    this.paintKenBurns(this.bgA, this.bgKeys[0]);

    if (this.bgKeys.length > 1) {
      this.time.addEvent({
        delay: BG_HOLD_MS,
        loop: true,
        callback: () => this.cycleBackground()
      });
    }

    this.add.rectangle(0, 0, w, h, 0x000000, 0.55).setOrigin(0, 0);

    // Decorative tile strip across the bottom (asphalt + dashes)
    const stripY = h - 80;
    for (let x = -8; x < Math.ceil(w / 32) + 1; x++) {
      const cx = x * 32 + 16;
      this.add.image(cx, stripY,      'cityatlas', 714).setDisplaySize(32, 32);
      this.add.image(cx, stripY + 32, 'cityatlas', x % 2 === 0 ? 712 : 714).setDisplaySize(32, 32);
      this.add.image(cx, stripY + 64, 'cityatlas', 705).setDisplaySize(32, 32);
    }

    // Animated cars driving across the strip
    this.cars = [];
    const carDefs = [
      { kind: 'veh_hatch_red',    dir: 1,  speed: 80,  startFrac: 0.10 },
      { kind: 'veh_taxi',         dir: -1, speed: 100, startFrac: 0.45 },
      { kind: 'veh_police',       dir: 1,  speed: 140, startFrac: 0.72 },
      { kind: 'veh_hatch_yellow', dir: -1, speed: 90,  startFrac: 0.95 },
      { kind: 'veh_hatch_green',  dir: 1,  speed: 70,  startFrac: 0.30 }
    ];
    for (const d of carDefs) {
      const s = this.add.sprite(w * d.startFrac, stripY + 16, d.kind).setScale(0.7);
      // Sprite faces "up" at rotation 0; +90° = face right (driving east), -90° = west
      s.setRotation(d.dir > 0 ? Math.PI / 2 : -Math.PI / 2);
      this.cars.push({ sprite: s, speed: d.speed * d.dir });
    }

    // A couple of peds wandering the pavement
    this.peds = [];
    const pedDefs = [
      { kind: 'ped_blue',     dir: 1,  speed: 28, startFrac: 0.20 },
      { kind: 'ped_woman',    dir: -1, speed: 32, startFrac: 0.62 },
      { kind: 'ped_survivor', dir: 1,  speed: 24, startFrac: 0.85 }
    ];
    for (const d of pedDefs) {
      const s = this.add.sprite(w * d.startFrac, stripY + 64, d.kind).setScale(0.45);
      s.setRotation(d.dir > 0 ? Math.PI / 2 : -Math.PI / 2);
      this.peds.push({ sprite: s, speed: d.speed * d.dir });
    }

    // Title — bigger + animated (bob, pulse, sway, rotate)
    this.titleBob = this.add.text(w / 2, h * 0.22, 'GTA', {
      fontSize: '128px', color: '#ffd84a', fontStyle: 'bold', fontFamily: 'monospace'
    }).setOrigin(0.5).setShadow(4, 4, '#000', 0, true, true);
    // Independent overlapping tweens give organic motion (no two cycles match up).
    this.tweens.add({
      targets: this.titleBob,
      y: h * 0.22 - 14,
      duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
    });
    this.tweens.add({
      targets: this.titleBob,
      scale: 1.12,
      duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
    });
    this.tweens.add({
      targets: this.titleBob,
      angle: 4,
      duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
    });
    this.tweens.add({
      targets: this.titleBob,
      x: w / 2 + 10,
      duration: 2300, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
    });

    this.add.text(w / 2, h * 0.34, 'LIVERPOOL', {
      fontSize: '54px', color: '#fff', fontStyle: 'bold', fontFamily: 'monospace'
    }).setOrigin(0.5).setShadow(2, 2, '#000', 0, true, true);

    this.add.text(w / 2, h * 0.42, 'unofficial scouseland edition', {
      fontSize: '14px', color: '#9ab', fontStyle: 'italic'
    }).setOrigin(0.5);

    // Play button
    this.playBtn = this.makeButton(w / 2, h * 0.58, 220, 56, 'PLAY', () => this.startGame());

    // Controls panel — two-column layout (keys right-aligned, descriptions left-aligned).
    const rows: [string, string][] = [
      ['WASD / arrows', 'move / drive'],
      ['F',             'enter / exit vehicle'],
      ['Space',         'punch (on foot)'],
      ['Esc',           'pause']
    ];
    const panel = this.add.container(w / 2, h * 0.74);
    const fontStyle = { fontSize: '13px', fontFamily: 'monospace' };
    const dashHalf = 16;   // horizontal half-gap between key column edge and dash
    const lineH = 20;
    const startY = -((rows.length - 1) * lineH) / 2;

    // First pass: build texts and track widest key + widest description.
    let keyMax = 0, descMax = 0;
    const made: Phaser.GameObjects.Text[] = [];
    for (let i = 0; i < rows.length; i++) {
      const y = startY + i * lineH;
      const key  = this.add.text(-dashHalf, y, rows[i][0], { ...fontStyle, color: '#ffd84a' }).setOrigin(1, 0.5);
      const dash = this.add.text(0,         y, '—',         { ...fontStyle, color: '#888'   }).setOrigin(0.5, 0.5);
      const desc = this.add.text( dashHalf, y, rows[i][1], { ...fontStyle, color: '#ccd'   }).setOrigin(0, 0.5);
      panel.add([key, dash, desc]);
      made.push(key, dash, desc);
      if (key.width  > keyMax)  keyMax  = key.width;
      if (desc.width > descMax) descMax = desc.width;
    }

    // Second pass: size the background to actual content width.
    const padX = 24, padY = 14;
    const innerW = keyMax + dashHalf * 2 + descMax;
    const innerH = rows.length * lineH;
    const panelBg = this.add.rectangle(0, 0, innerW + padX * 2, innerH + padY, 0xffffff, 0.04)
      .setStrokeStyle(1, 0xffffff, 0.08);
    panel.addAt(panelBg, 0); // behind the texts

    // Footer credits
    this.add.text(w / 2, h - 12, 'Sprites: Kenney CC0 · Built with Phaser 3', {
      fontSize: '11px', color: '#556'
    }).setOrigin(0.5, 1);

    // Keyboard shortcuts: Enter / Space starts the game
    this.input.keyboard!.once('keydown-ENTER', () => this.startGame());
    // Note: don't bind Space — too easy to fat-finger; PLAY click only otherwise.

    // Procedural menu music — A-minor synth loop. Stops when scene shuts down.
    MenuMusic.start();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => MenuMusic.stop());
  }

  update(_t: number, deltaMs: number) {
    const dt = deltaMs / 1000;
    const w = this.scale.width;
    const wrap = (m: Mover, padding: number) => {
      m.sprite.x += m.speed * dt;
      if (m.speed > 0 && m.sprite.x > w + padding) m.sprite.x = -padding;
      else if (m.speed < 0 && m.sprite.x < -padding) m.sprite.x = w + padding;
    };
    for (const c of this.cars) wrap(c, 60);
    for (const p of this.peds) wrap(p, 30);
  }

  private paintKenBurns(img: Phaser.GameObjects.Image, key: string) {
    const { width: w, height: h } = this.scale;
    img.setTexture(key);
    const tex = this.textures.get(key).getSourceImage();
    const baseScale = Math.max(w / tex.width, h / tex.height);
    const dx = (Math.random() < 0.5 ? -1 : 1) * BG_KB_PAN;
    const dy = (Math.random() < 0.5 ? -1 : 1) * BG_KB_PAN;
    this.tweens.killTweensOf(img);
    img.setScale(baseScale).setPosition(w / 2 - dx, h / 2 - dy);
    this.tweens.add({
      targets: img,
      scale: baseScale * (1 + BG_KB_ZOOM),
      x: w / 2 + dx,
      y: h / 2 + dy,
      duration: BG_HOLD_MS + BG_FADE_MS,
      ease: 'Sine.easeInOut'
    });
  }

  private cycleBackground() {
    this.bgIdx = (this.bgIdx + 1) % this.bgKeys.length;
    const nextKey = this.bgKeys[this.bgIdx];
    const showing = this.bgA.alpha >= this.bgB.alpha ? this.bgA : this.bgB;
    const hidden  = showing === this.bgA ? this.bgB : this.bgA;
    hidden.setAlpha(0);
    this.paintKenBurns(hidden, nextKey);
    this.tweens.add({ targets: hidden,  alpha: 1, duration: BG_FADE_MS, ease: 'Sine.easeInOut' });
    this.tweens.add({ targets: showing, alpha: 0, duration: BG_FADE_MS, ease: 'Sine.easeInOut' });
  }

  private startGame() {
    this.scene.start('GameScene');
    this.scene.launch('HudScene');
  }

  private makeButton(
    x: number, y: number, w: number, h: number, label: string, onClick: () => void
  ): Phaser.GameObjects.Container {
    const c = this.add.container(x, y);
    const bg = this.add.rectangle(0, 0, w, h, 0xffd84a, 1)
      .setStrokeStyle(3, 0x000000, 1);
    const text = this.add.text(0, 0, label, {
      fontSize: '24px', color: '#1a1410', fontStyle: 'bold', fontFamily: 'monospace'
    }).setOrigin(0.5);
    c.add([bg, text]);
    c.setSize(w, h);
    c.setInteractive({ useHandCursor: true });
    c.on('pointerover', () => bg.setFillStyle(0xffe885));
    c.on('pointerout',  () => bg.setFillStyle(0xffd84a));
    c.on('pointerdown', () => bg.setFillStyle(0xc9a830));
    c.on('pointerup',   () => { bg.setFillStyle(0xffe885); onClick(); });
    return c;
  }
}
